"use client";
import * as React from "react";
import { useRef, useState, useEffect } from "react";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import Image from "next/image";
import Link from "next/link";
import { Instructor } from "@/common/instructor/types";
import { BadgeIcon } from "@/components/ui/badgeIcon";
import parse from "html-react-parser";
import useTranslations from "@/i18n/useTranslations";

interface InstructorCardProps {
  instructor: Instructor;
  isFeatured?: boolean;
  location?: string;
}

const InstructorCard = ({
  instructor,
  isFeatured = false,
  location = "detail-page",
}: InstructorCardProps) => {
  const t = useTranslations("Program");
  const tCommon = useTranslations("Common");
  const [shouldReadMore, setShouldReadMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef?.current) {
      const contentHeight = Math.ceil(containerRef?.current?.scrollHeight);
      setShouldReadMore(contentHeight > containerRef?.current?.clientHeight);
    }
  }, [containerRef?.current?.scrollHeight]);

  const organizationName =
    instructor.organizationOverride ||
    instructor.partnerName ||
    instructor.partners?.[0]?.name ||
    "";

  const fullName = `${instructor.firstName}${instructor.lastName ? ` ${instructor.lastName}` : ""}`;

  const { elementClicked, elementViewed } = useElementEvents({
    webElement: {
      elementType: "featured-instructor",
      name: "featured-instructor",
      location: location,
      text: "featured-instructor",
      htmlId: "featured-instructor",
    },
  });

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  // There is HTML in some instructors' about me fields, this section
  // leaves them as is if that's the case to maintain the intended formatting
  // otherwise it wraps the text in <p></p>
  const rawAboutMe = instructor.aboutMe || "";
  const aboutMeWithParagraphs = /<p[\s>]/i.test(rawAboutMe)
    ? rawAboutMe
    : `<p>${rawAboutMe}</p>`;
  const aboutMe = parse(aboutMeWithParagraphs);

  return (
    <div
      className="border border-putty-400 rounded-xl bg-white overflow-hidden"
      ref={inViewRef}
    >
      {/* Main Content - Horizontal Layout */}
      <div className="flex flex-col md:flex-row gap-6 md:p-6 p-4">
        {/* Left Sidebar - Instructor Info */}
        <div className="flex flex-col items-start gap-4 mb-2 md:mb-0 w-[280px]">
          {/* Featured Badge */}
          {isFeatured && (
            <BadgeIcon icon="star-outline" withBorder={false}>
              {t("featuredInstructor")}
            </BadgeIcon>
          )}

          {/* Instructor Photo */}
          {instructor.imageUrl && (
            <div className="flex items-center justify-center border-2 rounded-full">
              <Image
                src={instructor.imageUrl}
                alt={fullName}
                title={fullName}
                width={90}
                height={90}
                className="w-[90px] h-[90px] object-cover rounded-full"
              />
            </div>
          )}

          {/* Name and Title */}
          <div>
            <h3 className="elm-h4 my-0 font-semibold text-secondary-500">
              {fullName}
            </h3>
            {(instructor.position || organizationName) && (
              <p className="text-base font-bold text-gray-800">
                {instructor.position}
                {instructor.position && organizationName && " at "}
                {organizationName}
              </p>
            )}
          </div>

          {/* Learn More Button */}
          <Link
            href={`/bio/${instructor.slug}`}
            target="_blank"
            className="inline-flex px-6 py-2 border-2 border-primary-500 rounded-full text-primary-500 hover:bg-primary-500 hover:text-white transition-colors text-center"
            onClick={() => elementClicked()}
          >
            {t("learnMoreAboutInstructor", { firstName: instructor.firstName })}
          </Link>
        </div>

        {/* Right Content - Bio */}
        {instructor.aboutMe && (
          <div className="flex-1 content-center border-t md:border-t-0 md:border-l border-putty-400 pt-6 md:pt-0 md:pl-6">
            <div
              className="md:text-base text-sm  text-gray-800 leading-relaxed whitespace-pre-line line-clamp-[11]"
              ref={containerRef}
            >
              {aboutMe}
            </div>
            {/* Read More Button */}
            {shouldReadMore && (
              <Link
                href={`/bio/${instructor.slug}`}
                target="_blank"
                className="md:text-base text-sm flex flex-start underline cursor-pointer pt-4"
                onClick={() => elementClicked()}
              >
                {tCommon("readMore")}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorCard;
